import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FetchResponse } from "src/app/shared/models/api-response.interface";
import { TopCategories } from "../../models/topcategories-interface";
import { environment } from "./../../../../../environments/environment";

@Component({
  selector: "app-home-topcategories",
  templateUrl: "./home-topcategories.component.html",
  styleUrls: ["./home-topcategories.component.scss"],
  standalone: false,
})
export class HomeTopcategoriesComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  host_url: string = environment.imageURL;

  @Input() topCategories!: FetchResponse<TopCategories[]>;
  @Input() headerKey = "home.categories.header";
  @Input() isPersonalize = true;
  @Input() activeCategoryId: string | null = null;
  @ViewChild("carouselTrack") carouselTrack?: ElementRef<HTMLDivElement>;

  currentIndex = 0;
  itemsPerView = 7.5;
  isPagedLayout = false;
  canScrollPrev = false;
  canScrollNext = false;
  progressThumbWidth = 35;
  progressThumbLeft = 0;

  private autoPlayId?: ReturnType<typeof setInterval>;
  private isPaused = false;

  get items(): TopCategories[] {
    return this.topCategories?.items ?? [];
  }

  getViewAllQueryParams(): Record<string, string | number | boolean> {
    if (this.isPersonalize) {
      return { personalize: true, pageIndex: 0 };
    }
    if (this.activeCategoryId) {
      return { category: this.activeCategoryId, pageIndex: 0 };
    }
    return { pageIndex: 0 };
  }

  getItemQueryParams(item: TopCategories): Record<string, string | number | boolean> {
    const params: Record<string, string | number | boolean> = {
      category: item.categoryId || item.id,
      pageIndex: 0,
    };
    if (item.id && item.id !== item.categoryId) {
      params["subCategory"] = item.id;
    }
    if (this.isPersonalize) {
      params["personalize"] = true;
    }
    return params;
  }

  get pagedItems(): TopCategories[][] {
    const list = this.items;
    const pageSize = 6;
    if (list.length === 0) {
      return [];
    }
    if (list.length <= pageSize) {
      return [list];
    }

    const pages: TopCategories[][] = [];
    for (let i = 0; i < list.length; i += pageSize) {
      if (i + pageSize <= list.length) {
        pages.push(list.slice(i, i + pageSize));
      } else {
        // Backfill from previous items so the final page always has a full 6-item (2x3) grid
        pages.push(list.slice(list.length - pageSize, list.length));
      }
    }
    return pages;
  }

  get maxSlideIndex(): number {
    if (this.isPagedLayout) {
      return Math.max(0, this.pagedItems.length - 1);
    }
    return Math.max(0, this.items.length - this.itemsPerView);
  }

  get slideIndicators(): number[] {
    const count = this.maxSlideIndex + 1;
    return count > 1 ? Array.from({ length: count }, (_, i) => i) : [];
  }

  get showNavControls(): boolean {
    if (this.isPagedLayout) {
      return this.items.length > 6;
    }
    return this.items.length > this.itemsPerView;
  }

  ngOnInit(): void {
    this.updateViewportMetrics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["topCategories"]) {
      this.currentIndex = 0;
      setTimeout(() => {
        this.updateViewportMetrics();
        this.startAutoPlay();
      });
    }
  }

  ngAfterViewInit(): void {
    this.updateViewportMetrics();
    this.startAutoPlay();
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.updateViewportMetrics();
  }

  trackByPageIndex(index: number): number {
    return index;
  }

  trackByCategoryId(_index: number, item: TopCategories): string {
    return item.id;
  }

  prevSlide(): void {
    const el = this.carouselTrack?.nativeElement;
    if (!el || this.items.length === 0) return;

    if (this.currentIndex <= 0 || el.scrollLeft <= 4) {
      this.goToSlide(this.maxSlideIndex);
    } else {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  nextSlide(): void {
    const el = this.carouselTrack?.nativeElement;
    if (!el || this.items.length === 0) return;

    const atEnd =
      this.currentIndex >= this.maxSlideIndex ||
      Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth - 4;

    if (atEnd) {
      this.goToSlide(0);
    } else {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  goToSlide(index: number): void {
    const el = this.carouselTrack?.nativeElement;
    if (!el) return;

    const clampedIndex = Math.max(0, Math.min(index, this.maxSlideIndex));
    const step = this.getSlideStepWidth(el);
    this.currentIndex = clampedIndex;

    const targetLeft =
      clampedIndex >= this.maxSlideIndex
        ? el.scrollWidth - el.clientWidth
        : clampedIndex * step;

    el.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: "smooth",
    });
  }

  onProgressTrackClick(event: MouseEvent): void {
    const trackEl = event.currentTarget as HTMLElement | null;
    if (!trackEl || this.maxSlideIndex <= 0) return;

    const rect = trackEl.getBoundingClientRect();
    const clickRatio = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / Math.max(1, rect.width))
    );
    const targetSlide = Math.round(clickRatio * this.maxSlideIndex);
    this.goToSlide(targetSlide);
  }

  onTrackScroll(): void {
    const el = this.carouselTrack?.nativeElement;
    if (!el) return;

    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const step = this.getSlideStepWidth(el);
    if (step > 0) {
      if (maxScroll > 0 && maxScroll - el.scrollLeft <= 6) {
        this.currentIndex = this.maxSlideIndex;
      } else {
        this.currentIndex = Math.min(
          this.maxSlideIndex,
          Math.max(0, Math.round(el.scrollLeft / step))
        );
      }
    }

    this.canScrollPrev = el.scrollLeft > 4;
    this.canScrollNext = maxScroll - el.scrollLeft > 4;

    const totalSteps = Math.max(1, this.maxSlideIndex + 1);
    this.progressThumbWidth = Math.max(
      24,
      Math.min(100, Math.round((1 / totalSteps) * 100))
    );
    const scrollRatio =
      maxScroll > 0 ? Math.min(1, Math.max(0, el.scrollLeft / maxScroll)) : 0;
    this.progressThumbLeft = scrollRatio * (100 - this.progressThumbWidth);
  }

  pauseAutoPlay(): void {
    this.isPaused = true;
  }

  resumeAutoPlay(): void {
    this.isPaused = false;
  }

  getImage(url: string | null | undefined): string {
    if (!url || url === "null" || url === "undefined") {
      return "";
    }

    if (url.startsWith("http")) {
      return url;
    }

    const baseUrl = this.host_url.endsWith("/")
      ? this.host_url
      : this.host_url + "/";
    const cleanUrl = url.startsWith("/") ? url.substring(1) : url;

    return baseUrl + cleanUrl;
  }

  getCategoryIcon(name: string | null | undefined): string {
    const normalized = (name ?? "").toLowerCase();
    if (normalized.includes("túi") || normalized.includes("bag") || normalized.includes("balo") || normalized.includes("ví")) {
      return "shopping_bag";
    }
    if (normalized.includes("ly") || normalized.includes("cốc") || normalized.includes("cup") || normalized.includes("mug")) {
      return "local_cafe";
    }
    if (normalized.includes("bình") || normalized.includes("chai") || normalized.includes("bottle")) {
      return "water_drop";
    }
    if (normalized.includes("bảng") || normalized.includes("biển") || normalized.includes("sign") || normalized.includes("banner")) {
      return "storefront";
    }
    if (normalized.includes("ốp") || normalized.includes("case") || normalized.includes("điện thoại")) {
      return "smartphone";
    }
    if (normalized.includes("nón") || normalized.includes("mũ") || normalized.includes("hat") || normalized.includes("cap")) {
      return "workspace_premium";
    }
    return "checkroom";
  }

  private updateViewportMetrics(): void {
    if (typeof window !== "undefined") {
      this.isPagedLayout = false;
      // On desktop (>= 992px), show seven full cards and half of the next card.
      this.itemsPerView = window.innerWidth < 640 ? 2.5 : window.innerWidth < 992 ? 4.5 : 7.5;
    }
    this.onTrackScroll();
  }

  private getSlideStepWidth(el: HTMLDivElement): number {
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap || "18") || 18;

    if (this.isPagedLayout) {
      const firstPage = el.querySelector<HTMLElement>(".categories__page");
      return (firstPage ? firstPage.offsetWidth : el.clientWidth) + gap;
    }

    const firstCard = el.querySelector<HTMLElement>(".categories__item");
    if (!firstCard) {
      return el.clientWidth / this.itemsPerView;
    }
    return firstCard.offsetWidth + gap;
  }

  private startAutoPlay(): void {
    this.stopAutoPlay();
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    this.autoPlayId = setInterval(() => {
      if (!this.isPaused && this.showNavControls) {
        this.nextSlide();
      }
    }, 4500);
  }

  private stopAutoPlay(): void {
    if (this.autoPlayId) {
      clearInterval(this.autoPlayId);
      this.autoPlayId = undefined;
    }
  }
}
